<template>
  <b-card
    body
    class="rounded-[8px] !shadow-none !-mt-[11px]"
  >
    <validation-observer
      ref="informasiGudangForm"
      #default
    >
      <b-form
        @submit.prevent="submitForm"
      >
        <div class="d-flex items-center justify-content-between mb-[16px]">
          <h2 class="text-black font-semibold text-[20px] mb-0 flex items-center gap-[8px]">
            Informasi Gudang <b-spinner
              v-if="isLoading"
              small
            />
          </h2>
          <div class="flex items-center gap-[8px]">
            <b-button
              variant="outline-primary"
              size="sm"
              :disabled="onEditMode || isLoading"
              @click="onEditMode = !onEditMode"
            >
              Edit
            </b-button>
            <b-button
              type="submit"
              :variant="onEditMode ? 'primary' : 'secondary'"
              size="sm"
              :disabled="!onEditMode"
            >
              Simpan
            </b-button>
          </div>
        </div>

        <div class="flex items-start flex-col md:flex-row gap-[16px] w-full">
          <div class="w-full grow">
            <ReusableInput
              v-model="form.warehouse_name"
              name="Nama Gudang"
              label="Nama Gudang"
              placeholder="Masukkan nama gudang"
              rules="required"
              :required="true"
              :disabled="enableEditInput"
            />
            <CIGTokoImage
              ref="cigTokoImage"
              :form="form"
              :enable-edit-input="enableEditInput"
            />
            <ReusableInput
              v-model="form.owner"
              name="Owner"
              label="Owner"
              placeholder="Masukkan nama owner"
              rules="required"
              :required="true"
              :disabled="enableEditInput"
            />
            <ReusableInput
              v-model="form.join_date"
              name="Mulai Bergabung"
              label="Mulai Bergabung"
              placeholder="Masukkan tanggal bergabung"
              rules="required"
              :required="true"
              disabled
            />
            <ReusableInput
              v-model="form.total_order"
              name="Jumlah Order"
              label="Jumlah Order"
              placeholder="Masukkan jumlah order"
              rules="required"
              :required="true"
              disabled
            />
            <CIGWarehouseCapacity
              :form="form"
              :enable-edit-input="enableEditInput"
            />
            <ReusableInput
              v-model="form.availability"
              name="Status"
              label="Status"
              placeholder="Pilih status gudang"
              rules="required"
              :disabled="enableEditInput"
            >
              <template #input="{ disabled, onInput }">
                <b-form-radio-group
                  v-model="form.availability"
                  :options="warehouseStatusOptions"
                  text-field="label"
                  :disabled="disabled"
                  @input="onInput"
                />
              </template>
            </ReusableInput>
          </div>
          <div class="w-full grow">
            <CIGLokasi
              :form="form"
              :enable-edit-input="enableEditInput"
            />
            <CIGWarehouseImage
              ref="cigWarehouseImage"
              :form="form"
              :enable-edit-input="enableEditInput"
            />
            <ReusableInput
              v-model="form.detail_address"
              name="Detail Alamat"
              label="Detail Alamat"
              placeholder="Masukkan detail alamat"
              rules="required"
              :required="true"
              :disabled="enableEditInput"
            >
              <template #input="{ value, disabled, onInput, onBlur, onFocus }">
                <b-form-textarea
                  :value="value"
                  :disabled="disabled"
                  placeholder="Masukkan detail alamat"
                  rows="3"
                  class="!text-[#333333] border border-[#E2E2E2] disabled:!bg-[#F4F4F4] !py-[10px]"
                  style="border-color: #E2E2E2!important;"
                  @input="onInput($event)"
                  @blur="onBlur($event)"
                  @focus="onFocus($event)"
                />
              </template>
            </ReusableInput>
            <ReusableInput
              v-model="form.description"
              name="Deskripsi Gudang"
              label="Deskripsi Gudang"
              placeholder="Masukkan deskripsi gudang"
              rules="required|max:250"
              :required="true"
              :disabled="enableEditInput"
            >
              <template #input="{ value, disabled, onInput, onBlur, onFocus }">
                <b-form-textarea
                  :value="value"
                  :disabled="disabled"
                  placeholder="Masukkan deskripsi gudang"
                  rows="6"
                  class="!text-[#333333] border border-[#E2E2E2] disabled:!bg-[#F4F4F4] !py-[10px]"
                  style="border-color: #E2E2E2!important;"
                  @input="onInput($event)"
                  @blur="onBlur($event)"
                  @focus="onFocus($event)"
                />
              </template>
            </ReusableInput>
          </div>
        </div>
      </b-form>
    </validation-observer>
    <CIGOperationalTime
      :on-edit-mode="onEditMode"
      :warehouse-id="warehouseId"
    />
    <ModalGeneralAction ref="mgac_kompack" />
  </b-card>
</template>

<script>
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { DAY_MONTH_YEAR } from '@/libs/filterDate'
import { toastError, toastSuccess } from '@/libs/toastification'
import ReusableInput from './Input.vue'
import { quantityTypeOptions, warehouseStatusOptions } from '../config'
import CIGWarehouseImage from './CIGWarehouseImage.vue'
import CIGWarehouseCapacity from './CIGWarehouseCapacity.vue'
import CIGOperationalTime from './CIGOperationalTime.vue'
import CIGLokasi from './CIGLokasi.vue'
import ModalGeneralAction from './ModalGeneralAction.vue'
import CIGTokoImage from './CIGTokoImage.vue'

/**
 * CardInformasiGudang
 * @description CardInformasiGudang is a component
 * that displays, edit and delete the information of the warehouse detail profile
 * @param {Number} warehouseId - The id of the warehouse
 * @returns {Object} CardInformasiGudang
 */
export default {
  name: 'CardInformasiGudang',
  components: {
    ValidationObserver,
    ReusableInput,
    CIGWarehouseImage,
    CIGWarehouseCapacity,
    CIGOperationalTime,
    CIGLokasi,
    CIGTokoImage,
    ModalGeneralAction,
  },
  props: {
    warehouseId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      mitra_id: this?.$store?.state?.auth?.userData?.mitras?.id,
      user_id: this?.$store?.state?.auth?.userData?.id,
      form: {
        warehouse_name: '',
        owner: '',
        image_warehouse: [],
        join_date: DAY_MONTH_YEAR('2021-01-01'),
        total_order: 0,
        capacity: null,
        capacity_unit: null,
        availability: 'Penuh',
        location_link: '',
        location_name: '',
        detail_address: '',
        description: '',
      },
      isLoading: true,
      onEditMode: false,
      required,
      quantityTypeOptions,
      warehouseStatusOptions,
    }
  },
  computed: {
    enableEditInput() {
      return !this.onEditMode
    },
  },
  watch: {
    warehouseId(newVal) {
      if (newVal >= 0) {
        this.fetchData()
      }
    },
  },
  methods: {
    /**
     * Validate the form, take user confirmation and submit the data
     */
    async submitForm() {
      const { isValid: isImageWarehouseValid, data: imageWarehouseData } = this.$refs.cigWarehouseImage.validate()
      const { isValid: isImageTokoValid, data: imageLogoData } = this.$refs.cigTokoImage.validate()
      const formValidation = await this.$refs.informasiGudangForm.validate()
      if (!isImageWarehouseValid) {
        return
      }
      if (!isImageTokoValid) {
        return
      }
      if (!formValidation) {
        return
      }
      const mappedFormData = this.mappedFormData({
        form: this.form,
        imageLogoData,
        newImagesWarehouseData: imageWarehouseData.new,
        deletedImagesWarehouseData: imageWarehouseData.deleted,
      })

      this.$refs.mgac_kompack.show({
        title: 'Konfirmasi',
        message: 'Apakah Anda yakin ingin menyimpan perubahan?',
        onConfirm: ({ hide: closeModal, setIsLoading }) => {
          setIsLoading(true)
          this.mutateInformasiGudang(mappedFormData).then(() => {
            this.fetchData()
            closeModal()
            this.onEditMode = false
            this.$refs.cigWarehouseImage.refreshImages()
            toastSuccess('Data informasi gudang berhasil disimpan')
          }).catch(() => {
            toastError('Gagal mengubah data informasi gudang, silahkan refresh untuk coba lagi')
          }).finally(() => {
            setIsLoading(false)
          })
        },
      })
    },
    /**
     * Parse the availability
     * @param {String} availability - The availability
     * @returns {Number} The availability
     */
    parseAvailability(availability) {
      return availability === 'Tersedia' ? 1 : 0
    },
    /**
     * Fetch the data and map the data to the component
     */
    fetchData() {
      this.isLoading = true
      this.$http.get(`/kompack/warehouse/information/${this.mitra_id}`)
        .then(({ data }) => {
          const dt = data.data

          this.form = {
            ...this.form,
            ...dt,
            join_date: DAY_MONTH_YEAR(dt.join_date),
            capacity: dt.capacity || null,
            availability: this.parseAvailability(dt.availability),
          }
        })
        .catch(() => {
          toastError('Gagal mengambil data informasi gudang, silahkan refresh untuk coba lagi')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    /**
     * Handle the image error
     * @param {Event} event - The event
     */
    handleImageError(event) {
      const fallbackSrc = 'https://storage.googleapis.com/komerce/assets/icons/product-placehold.svg'
      const imgElement = event.target
      imgElement.src = fallbackSrc
    },
    /**
     * Mutate the information of the warehouse
     * @param {Object} formData - The form data
     * @returns {Promise<Object>} The response
     */
    async mutateInformasiGudang(formData) {
      return this.$http_kompack.post('/v1/kompack/setting/update/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    },
    /**
     * Map the form data
     * @param {Object} form - The form data
     * @param {Object} imageLogoData - The image logo data
     * @param {Array} newImagesWarehouseData - The new images warehouse data
     * @param {Array} deletedImagesWarehouseData - The deleted images warehouse data
     * @returns {Object} The form data
     */
    mappedFormData({
      form,
      imageLogoData,
      newImagesWarehouseData,
      deletedImagesWarehouseData,
    }) {
      const formData = new FormData()
      formData.append('id_warehouse', this.warehouseId)
      formData.append('name', form.warehouse_name)
      formData.append('owner', form.owner)
      formData.append('capacity', form.capacity)
      formData.append('capacity_type', form.capacity_type)
      formData.append('availability', form.availability)
      formData.append('location_link', form.location_link)
      formData.append('location_name', form.location_name)
      formData.append('detail_address', form.detail_address)
      formData.append('description', form.description)
      formData.append('latitude', form.latitude)
      formData.append('longitude', form.longitude)

      if (imageLogoData) {
        formData.append('image_logo', imageLogoData)
      }

      if (newImagesWarehouseData) {
        newImagesWarehouseData.forEach(x => {
          formData.append('image_warehouse_insert[]', x)
        })
      }

      if (deletedImagesWarehouseData) {
        deletedImagesWarehouseData.forEach(x => {
          formData.append('image_warehouse_delete[]', x)
        })
      }

      return formData
    },
  },
}
</script>
